import React, { useState, useEffect } from 'react'; // Import useEffect
import { connect, WithStore, MapStateToProps, MapDispatchToProps } from '../../redux/services/Imports';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/pagination';
import { Swiper as TypeSwiper } from 'swiper';
import { Props } from './interfaces';
import './SliderNewsList.scss';
import './SliderNewsList-media.scss';
import PaginationBlock from '../Pagination/Pagination';
import { CardNews } from '../../redux/services/GlobalFunctionsTsx.ts';

const SliderNewsList = ({ slides, title, bg = false, widthWindow }: Props) => {
  const [numActiveSlide, setNumActiveSlide] = useState(1);

  const Changed = (current: TypeSwiper) => {
    let newSlideNumber;
    if (typeof current.realIndex === 'number' && !isNaN(current.realIndex)) {
      newSlideNumber = current.realIndex + 1;
    } else if (typeof current.activeIndex === 'number' && !isNaN(current.activeIndex)) {
      // Fallback to activeIndex if realIndex is NaN (often happens if loop is problematic)
      newSlideNumber = current.activeIndex + 1;
    } else {
      newSlideNumber = 1; // Absolute fallback
    }
    setNumActiveSlide(newSlideNumber);
  };

  const [slider, setSlider] = useState<TypeSwiper>();

  // Effect to update Swiper instance when it's ready or when relevant props change
  useEffect(() => {
    if (slider && !slider.destroyed) { // Check slider existence and ensure it's not destroyed
      // Delaying the update can help if the DOM isn't fully ready or dimensions are not final
      // when the effect initially runs, especially with conditional rendering by MediaContent.
      const timerId = setTimeout(() => {
        if (slider && !slider.destroyed) { // Double check inside timeout
          slider.updateSize(); // Recalculate Swiper size and slides sizes
          slider.update();     // Recalculate Swiper progress, translate, etc.
          if (settings.loop) { // Check if loop is currently enabled in settings
            slider.loopFix();    // Force to fix loop when dynamic slides addition/removal or layout changes occur.
          }

          let targetIndex = 0;
          if (settings.loop && typeof slider.realIndex === 'number' && !isNaN(slider.realIndex)) {
            targetIndex = slider.realIndex;
          } else if (typeof slider.activeIndex === 'number' && !isNaN(slider.activeIndex)) {
            targetIndex = slider.activeIndex;
          }

          if (settings.loop) {
            slider.slideToLoop(targetIndex, 0); // 0ms duration for no animation
          } else {
            slider.slideTo(targetIndex, 0); // 0ms duration for no animation
          }
        }
      }, 250); // Slightly increased delay to 250ms
      return () => {
        clearTimeout(timerId); // Cleanup timeout on unmount or re-run
      };
    }
  }, [slider, slides, widthWindow]); // Dependencies: slider instance, slides data, and window width

  const slidesPerViewComputed = widthWindow > 720 ? 3 : 1;
  // Enable loop only if there are enough slides for it to make sense
  const enableLoop = slides.length > slidesPerViewComputed;

  const settings = {
    autoplay: false,
    speed: 500,
    loop: enableLoop, // Conditional loop
    modules: [Autoplay],
    slidesPerView: slidesPerViewComputed,
    onSlideChange: Changed,
    className: 'slider-news-list',
    spaceBetween: 20,
    observer: true, // Re-enabled: Watch for changes in children (slides)
    observeParents: true, // Re-enabled: Watch for changes in parent elements
  };

  const prevSlide = () => {
    // Ensure slider instance is valid before calling methods
    if (slider && !slider.destroyed) {
      slider.slidePrev(500, false);
    }
  };
  const nextSlide = () => {
    // Ensure slider instance is valid before calling methods
    if (slider && !slider.destroyed) {
      slider.slideNext(500, false);
    }
  };

  return (
    <div className={`slider-news-list__container${bg ? ' slider-news-list__container_bg' : ''}`}>
      <h2 className="slider-news-list__title">{title}</h2>

      <Swiper
        {...settings}
        onInit={(swiperInstance: TypeSwiper) => {
          setSlider(swiperInstance);
        }}
      >
        {slides.map((item, i) => (
          // Assuming item has a unique 'id' property. If not, revert to key={i}
          // but the observer options are the more critical change here.
          <SwiperSlide key={item.id || i}>
            <CardNews item={item} />
          </SwiperSlide>
        ))}
      </Swiper>

      {slides.length > 3 ? (
        <div className="slider-news-list__dots">
          <PaginationBlock current={numActiveSlide} all={slides.length} />
          <div className="slider-news-list__arrow slider-news-list__arrow_prev" onClick={prevSlide}></div>
          <div className="slider-news-list__arrow slider-news-list__arrow_next" onClick={nextSlide}></div>
        </div>
      ) : null}
    </div>
  );
};

export default WithStore()(connect(MapStateToProps, MapDispatchToProps)(SliderNewsList));
